/**
 * WB4-CP — Domain workflow action palette seeds (Act group · contextual ranking).
 *
 * SSOT for workbench-bootstrap domain `kind: 'action'` registry rows.
 * @see workbench-command-palette-domain-density.ts PALETTE_DENSITY_PILOT_DOMAINS
 */

import type { CommandEntry } from './command-registry';
import { DOMAIN_ACTION_HANDLER_REFS } from './domain-action-rule-table';

export interface PaletteDomainActionSeed {
  id: string;
  label: string;
  actionRef: string;
  businessDomains: readonly string[];
  keywords?: readonly string[];
  priority?: number;
}

/** Top workflow actions per pilot domain — palette Act group (object-context optional). */
export const PALETTE_DOMAIN_ACTION_COMMAND_SEEDS: readonly PaletteDomainActionSeed[] = [
  {
    id: 'act:order:start-workflow',
    label: '주문 워크플로우',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startOrderWorkflow,
    businessDomains: ['order', 'sales_order'],
    keywords: ['order', 'approve', '주문', '승인', 'workflow'],
    priority: 72,
  },
  {
    id: 'act:order:approve-order',
    label: '주문 승인',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startOrderWorkflow,
    businessDomains: ['order', 'sales_order'],
    keywords: ['approve', 'order', '승인', '주문'],
    priority: 75,
  },
  {
    id: 'act:collection:start-accounting',
    label: '회계 워크플로우',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startFinanceAccounting,
    businessDomains: ['collection', 'ar_collection'],
    keywords: ['collection', 'accounting', '수금', '회계'],
    priority: 71,
  },
  {
    id: 'act:collection:approve-follow-up',
    label: '수금 승인 Flow',
    actionRef: 'workbench:intent:collect_payment',
    businessDomains: ['collection', 'ar_collection'],
    keywords: ['approve', 'collection', 'follow-up', '승인', '수금'],
    priority: 72,
  },
  {
    id: 'act:task:start-assignment',
    label: '배정 워크플로우',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startAssignmentWorkflow,
    businessDomains: ['task_execution', 'assignment_execution'],
    keywords: ['task', 'assignment', '작업', '배정'],
    priority: 70,
  },
  {
    id: 'act:finance:start-accounting',
    label: '재무 회계 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startFinanceAccounting,
    businessDomains: ['finance_state', 'finance_flow', 'finance_budget'],
    keywords: ['finance', 'accounting', '재무', '회계'],
    priority: 69,
  },
  {
    id: 'act:finance:approve-accounting',
    label: '회계 승인 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startFinanceAccounting,
    businessDomains: ['finance_state', 'finance_flow', 'finance_budget', 'stream_execution'],
    keywords: ['approve', 'accounting', '승인', '회계', 'finance'],
    priority: 74,
  },
  {
    id: 'act:customer:start-order',
    label: '고객 주문 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startOrderWorkflow,
    businessDomains: ['customer', 'counterparty'],
    keywords: ['customer', 'order', '고객', '주문'],
    priority: 68,
  },
  {
    id: 'act:customer:approve-order',
    label: '주문 승인 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startOrderWorkflow,
    businessDomains: ['customer', 'counterparty', 'external_entity'],
    keywords: ['approve', 'order', '승인', '주문', 'customer'],
    priority: 73,
  },
  {
    id: 'act:supplier:start-inventory',
    label: '공급 재고 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow,
    businessDomains: ['supplier'],
    keywords: ['supplier', 'inventory', '공급', '재고'],
    priority: 67,
  },
  {
    id: 'act:supplier:approve-receiving',
    label: '입고 승인 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow,
    businessDomains: ['supplier'],
    keywords: ['approve', 'supplier', 'receiving', '승인', '입고'],
    priority: 71,
  },
  {
    id: 'act:inventory:start-workflow',
    label: '재고 워크플로우',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow,
    businessDomains: ['inventory_shortage'],
    keywords: ['inventory', 'shortage', '재고', '부족'],
    priority: 66,
  },
  {
    id: 'act:inventory:approve-adjustment',
    label: '재고 조정 승인 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow,
    businessDomains: ['inventory_shortage'],
    keywords: ['approve', 'inventory', 'adjustment', '승인', '재고', '조정'],
    priority: 70,
  },
  {
    id: 'act:warehouse:start-workflow',
    label: '창고 피킹 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow,
    businessDomains: ['warehouse_picking_list'],
    keywords: ['warehouse', 'picking', '창고', '피킹'],
    priority: 65,
  },
  {
    id: 'act:warehouse:approve-picking',
    label: '피킹 승인 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startInventoryWorkflow,
    businessDomains: ['warehouse_picking_list'],
    keywords: ['approve', 'warehouse', 'picking', '승인', '피킹', '창고'],
    priority: 71,
  },
  {
    id: 'act:manufacturing:start-production',
    label: '생산 워크플로우',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startProductionWorkflow,
    businessDomains: ['manufacturing_bom', 'production_order', 'quality_inspection'],
    keywords: ['manufacturing', 'production', '생산', 'bom'],
    priority: 64,
  },
  {
    id: 'act:manufacturing:approve-production',
    label: '생산 승인 Flow',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.startProductionWorkflow,
    businessDomains: ['manufacturing_bom', 'production_order', 'quality_inspection'],
    keywords: ['approve', 'production', '승인', '생산'],
    priority: 70,
  },
  {
    id: 'act:document:attach-evidence',
    label: '증빙 첨부',
    actionRef: DOMAIN_ACTION_HANDLER_REFS.attachDocumentEvidence,
    businessDomains: ['knowledge_report', 'document', 'document_report'],
    keywords: ['document', 'evidence', '문서', '증빙'],
    priority: 63,
  },
] as const;

export function buildPaletteDomainActionCommandEntries(): CommandEntry[] {
  return PALETTE_DOMAIN_ACTION_COMMAND_SEEDS.map((seed) => ({
    id: seed.id,
    label: seed.label,
    keywords: [...(seed.keywords ?? [])],
    kind: 'action' as const,
    actionRef: seed.actionRef,
    paletteGroup: 'act' as const,
    planes: ['business'] as const,
    businessDomains: [...seed.businessDomains],
    priority: seed.priority ?? 60,
  }));
}
